import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Share2, X, FileText, Send } from 'lucide-react'

// Bump the version suffix to re-announce a future change to the share flow.
const STORAGE_KEY = 'avi_reports_share_howto_v1'

/**
 * One-time "how to share a report" bubble on the Reports page. Explains the Save & Share
 * flow and points up at the Available tab. Dismissal is persisted in localStorage so it
 * shows only once per browser (until the user closes it).
 */
const ShareReportBubble: React.FC = () => {
  const [show, setShow] = useState<boolean>(() => {
    try { return !localStorage.getItem(STORAGE_KEY) } catch { return true }
  })

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1') } catch { /* localStorage unavailable */ }
    setShow(false)
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          className="relative z-30 mb-6 max-w-lg"
        >
          {/* Speech-bubble pointer, roughly under the Available tab */}
          <div className="absolute -top-1.5 left-10 h-3 w-3 rotate-45 bg-white border-l border-t border-indigo-200" />
          <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-white via-indigo-50/40 to-violet-50/40 shadow-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center flex-shrink-0 shadow">
                  <Share2 className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">New: share a report with a link</h3>
                  <p className="text-xs text-gray-600 mt-1">Freeze a report as a snapshot and send it to anyone — no login needed.</p>
                  <ol className="mt-2 space-y-1.5">
                    <li className="flex items-start gap-2 text-xs text-gray-700">
                      <FileText className="h-3.5 w-3.5 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <span>Pick a report from the list below and set your parameters.</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs text-gray-700">
                      <Share2 className="h-3.5 w-3.5 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <span>Click <span className="font-semibold">Save &amp; Share</span> (next to Download PDF).</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs text-gray-700">
                      <Send className="h-3.5 w-3.5 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <span>Add recipients to email the link, or copy it to share yourself. Manage sent links on the <span className="font-semibold">Shared</span> tab.</span>
                    </li>
                  </ol>
                  <button
                    onClick={dismiss}
                    className="mt-3 inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-xs font-semibold hover:from-indigo-700 hover:to-violet-700 transition-colors shadow-sm"
                  >
                    Got it
                  </button>
                </div>
              </div>
              <button onClick={dismiss} title="Dismiss" aria-label="Dismiss" className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ShareReportBubble
